// src/utils/tactic/draftImport.js
// ─────────────────────────────────────────────────────────────────────────
// Turn a game parsed out of the uploaded workbook into a starting board.
//
// The workbook rows already carry role -> hero for both teams (see
// excelParser's `fsByRole` / `opByRole`), so importing a draft is a matter of
// resolving those five roles per side and dropping each hero on its lane.
//
// The board speaks the team's own draft vocabulary (DSL/JUG/MID/ADL/ROAM); the
// workbook's SUP column is translated on the way in.
// ─────────────────────────────────────────────────────────────────────────

import { canonicalHeroName } from "../heroCatalog";
import { LANES, LANE_OF_SHEET_ROLE } from "./heroLanes";
import { createHeroToken } from "./playbook";

export { LANES } from "./heroLanes";

const SHEET_ROLES = Object.keys(LANE_OF_SHEET_ROLE);

// Lane starting spots for the blue side, in logical coords. The red side is
// the 180° rotation of these, which is exactly how the map is built.
const BLUE_START = {
  DSL: [0.085, 0.300],
  JUG: [0.300, 0.420],
  MID: [0.235, 0.235],
  ADL: [0.300, 0.085],
  ROAM: [0.395, 0.105],
};

export function startPositionFor(lane, team) {
  const [x, y] = BLUE_START[lane] || [0.2, 0.2];
  return team === "red" ? [1 - x, 1 - y] : [x, y];
}

/** Lane -> hero for one side of a parsed game, tolerating missing columns. */
function lanePicks(game, side) {
  const byRole = side === "fs" ? game?.fsByRole : game?.opByRole;
  const flat = side === "fs" ? game?.fsPick : game?.opPick;

  return SHEET_ROLES.reduce((acc, role, i) => {
    const hero = byRole?.[role] || (Array.isArray(flat) ? flat[i] : "");
    acc[LANE_OF_SHEET_ROLE[role]] = hero ? canonicalHeroName(hero) : "";
    return acc;
  }, {});
}

/** Short label for the game picker list. */
export function describeGame(game, index) {
  const date = game?.dateStr || "";
  const opp = game?.competitor || "ไม่ทราบคู่แข่ง";
  const type = game?.type ? ` · ${game.type}` : "";
  const side = game?.side ? ` · ${game.side}` : "";
  const result = game?.win ? " · ชนะ" : game?.lose ? " · แพ้" : "";
  return `${date || `เกม ${index + 1}`} · ${opp}${type}${side}${result}`;
}

/**
 * Build the ten hero tokens for a game.
 *
 * `ourSide` decides which half of the map our team starts on; the opponent
 * always takes the other half.
 */
export function tokensFromGame(game, ourSide = "blue") {
  if (!game) return [];

  const oppSide = ourSide === "blue" ? "red" : "blue";
  const ours = lanePicks(game, "fs");
  const theirs = lanePicks(game, "op");

  const build = (picks, team) =>
    LANES.filter((lane) => picks[lane]).map((lane) => {
      const [x, y] = startPositionFor(lane, team);
      return createHeroToken({ heroName: picks[lane], team, role: lane, x, y, t: 0 });
    });

  return [...build(ours, ourSide), ...build(theirs, oppSide)];
}

// The screenshot parser (api/parse-draft-image.js) speaks the workbook's column
// names, so a parsed row maps onto lanes exactly the way a workbook row does.
const IMAGE_ROW_LANES = [
  ["DSL", "bruDSL", "opDSL"],
  ["JUG", "bruJUG", "opJUG"],
  ["MID", "bruMID", "opMID"],
  ["ADL", "bruADL", "opADL"],
  ["ROAM", "bruSUP", "opSUP"],
];

/**
 * Ten tokens from one row of a parsed draft screenshot.
 * Heroes the parser could not read are simply left off the board.
 */
export function tokensFromImageRow(row, ourSide = "blue") {
  if (!row) return [];
  const oppSide = ourSide === "blue" ? "red" : "blue";

  const build = (field, team) =>
    IMAGE_ROW_LANES.map(([lane, ourField, theirField]) => {
      const raw = row[field === "ours" ? ourField : theirField];
      if (!raw) return null;
      const [x, y] = startPositionFor(lane, team);
      return createHeroToken({
        heroName: canonicalHeroName(raw),
        team,
        role: lane,
        x,
        y,
        t: 0,
      });
    }).filter(Boolean);

  return [...build("ours", ourSide), ...build("theirs", oppSide)];
}

/** One-line description of a parsed row, for the confirmation step. */
export function describeImageRow(row) {
  const parts = [row?.date, row?.gameType, row?.gameNo ? `เกม ${row.gameNo}` : ""].filter(Boolean);
  return parts.join(" · ") || "ดราฟจากรูป";
}

/** The side the workbook recorded us on, so import can default sensibly. */
export function sideFromGame(game) {
  const raw = String(game?.side || "").trim().toLowerCase();
  return raw === "red" ? "red" : "blue";
}

/** Bans, shown as context next to an imported draft. */
export function bansFromGame(game) {
  const clean = (list) =>
    (Array.isArray(list) ? list : [])
      .map((h) => (h ? canonicalHeroName(h) : ""))
      .filter(Boolean);
  return {
    ours: clean(game?.draftRecapFsBan?.length ? game.draftRecapFsBan : game?.fsBan),
    theirs: clean(game?.draftRecapOpBan?.length ? game.draftRecapOpBan : game?.opBan),
  };
}
